import { margin, percent, px, rem } from "csx";
import { Content, Stretch } from "gls/lib";
import moment, { Moment } from "moment";
import { FC, useMemo, useState } from "react";
import { DateRangePicker as DRP, FocusedInputShape } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { useTranslation } from "react-i18next";
import { style } from "typestyle";
import { Colours, Sizes, boxShadow, fontFamily } from "../../styles";
import { Button } from "../Button/Button";

export interface DateRangePickerProps {
  endDate: moment.Moment | null;
  startDate: moment.Moment | null;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  showClearDates?: boolean;
  withConfirmButton?: boolean;
  confirmButtonText?: string;
  onChange: (arg: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => void;
}

export const DateRangePicker: FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  showClearDates,
  withConfirmButton,
  confirmButtonText,
  onChange,
}) => {
  const { t } = useTranslation();

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  );

  const [start, setStart] = useState<Moment | null>(startDate);
  const [end, setEnd] = useState<Moment | null>(endDate);

  const showClear = useMemo(
    () => !!(startDate || endDate) && showClearDates,
    [startDate, endDate],
  );

  const onDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  }) => {
    if (focusedInput === "startDate") {
      setFocusedInput("endDate");
      setStart(startDate);
      setEnd(null);
    } else {
      setEnd(endDate);
      setFocusedInput("startDate");
    }

    !withConfirmButton && onChange({ startDate, endDate });
  };

  const handleOutsideRange = (day: moment.Moment): boolean => {
    return (
      !!(minDate && day.isBefore(minDate)) ||
      !!(maxDate && day.isAfter(maxDate))
    );
  };

  return (
    <Content className={drpWrapper} data-testid="date-range-picker">
      <DRP
        horizontalMargin={32}
        isOutsideRange={handleOutsideRange}
        displayFormat="DD-MM-YYYY"
        focusedInput={focusedInput}
        onFocusChange={setFocusedInput}
        endDateId="endDate"
        startDateId="startDate"
        startDate={start}
        endDate={end}
        onDatesChange={onDatesChange}
        hideKeyboardShortcutsPanel
        showClearDates={showClear}
        reopenPickerOnClearDates={false}
        maxDate={maxDate}
        minDate={minDate}
        minimumNights={0}
        transitionDuration={0}
        keepOpenOnDateSelect={withConfirmButton}
        onClose={() => {
          setFocusedInput(null);
          setStart(startDate);
          setEnd(endDate);
        }}
        renderCalendarInfo={
          withConfirmButton
            ? () => {
                return (
                  <Stretch
                    horizontalAlign="right"
                    padding={[0, rem(1), rem(1), rem(1)]}
                  >
                    <Button
                      appearance="blue"
                      content={confirmButtonText || t("Select")}
                      size="small"
                      disabled={!start || !end}
                      onClick={() => {
                        onChange({
                          startDate: start,
                          endDate: end,
                        });

                        setFocusedInput(null);
                      }}
                    />
                  </Stretch>
                );
              }
            : undefined
        }
      />
    </Content>
  );
};

const drpWrapper = style({
  $nest: {
    "& *": {
      fontFamily: fontFamily,
    },
    "& .DayPicker": {
      boxShadow,
    },
    "& .DateRangePickerInput__withBorder": {
      background: Colours.darkBlue,
      borderRadius: Sizes.borderRadius,
      border: `1px solid ${Colours.blue}`,
      padding: `0 ${rem(0.5)}`,
    },
    "& .DateRangePicker_picker": {
      zIndex: 1000,
    },
    "& .DateRangePicker_picker, & .CalendarMonth, & .CalendarMonthGrid, & .DayPicker__horizontal":
      {
        background: Colours.darkBlue,
      },
    "& .DateInput_fangStroke": {
      stroke: Colours.darkBlue,
      fill: Colours.darkBlue,
    },
    "& .DayPickerNavigation_button": {
      backgroundColor: Colours.blue,
      border: `1px solid ${Colours.textLight}`,
    },
    "& .DayPickerNavigation_svg__horizontal": {
      fill: Colours.textLight,
    },
    "& .DayPicker_weekHeader": {
      color: Colours.textLight,
    },
    "& .DayPicker_weekHeader_li": {
      fill: Colours.textLight,
    },
    "& .CalendarMonth_caption": {
      color: Colours.textLight,
    },
    "& .DateRangePickerInput_arrow": {
      margin: margin(0, rem(0.5)),
    },
    "& .DateInput": {
      width: "auto",
    },
    "& .DateInput_input": {
      color: Colours.textLight,
      width: rem(5.5),
      padding: px(5),
      borderBottom: 0,
      fontSize: px(16),
      background: Colours.darkBlue,
    },
    "& .DateInput_input__focused::after": {
      position: "absolute",
      content: "''",
      width: percent(100),
      height: px(2),
      background: Colours.primary,
      // borderBottom: `${px(2)} solid ${Colours.primary}`
    },
    "& .DateRangePickerInput__showClearDates": {
      paddingRight: 0,
    },
    "& .DayPickerNavigation_button__disabled": {
      border: 0,
      display: "none",
    },
    "& .DateRangePickerInput_arrow_svg": {
      fill: Colours.textLight,
      height: px(20),
      width: px(20),
    },
    "& .DateRangePickerInput_clearDates_default:focus, & .DateRangePickerInput_clearDates_default:hover":
      {
        background: "none",
        $nest: {
          "& .DateRangePickerInput_clearDates_svg": {
            fill: Colours.blue,
          },
        },
      },
    "& .DateRangePickerInput_clearDates": {
      position: "relative",
      right: "auto",
      top: "auto",
      transform: "none",
      margin: 0,
    },
    "& .DateRangePickerInput_clearDates_svg": {
      fill: Colours.textLight,
    },

    "& .CalendarDay__default": {
      background: Colours.blue,
      color: Colours.textLight,
      border: `1px solid ${Colours.lightBlue}`,
    },
    "& .CalendarDay__default:hover": {
      background: Colours.lighterBlue,
      color: "white",
    },
    "& .CalendarDay__blocked_out_of_range, & .CalendarDay__blocked_out_of_range:hover":
      {
        backgroundColor: Colours.darkerBlue,
      },
    "& .CalendarDay__blocked_out_of_range, & .CalendarDay__blocked_out_of_range:active, & .CalendarDay__blocked_out_of_range:hover":
      {
        color: Colours.textDark,
        border: `1px solid ${Colours.darkBlue}`,
      },
    "& .CalendarDay__selected_span": {
      background: Colours.lightestBlue,
      color: "white",
      border: `1px solid ${Colours.lighterBlue}`,
    },
    "& .CalendarDay__selected_span:hover": {
      background: Colours.primary,
    },
    "& .CalendarDay__selected": {
      background: Colours.primary,
      color: "white",
      border: `1px solid ${Colours.lightestBlue}`,
    },

    "& .CalendarDay__selected:hover": {
      border: `1px solid ${Colours.lightBlue}`,
      background: Colours.lightBlue,
      color: "white",
    },
    "& .CalendarDay__hovered_span:hover, & .CalendarDay__hovered_span": {
      background: Colours.blue,
      color: "white",
      border: `1px solid ${Colours.blue}`,
    },
    "& .CalendarDay__hovered_span:hover": {
      background: Colours.lightestBlue,
    },
  },
});
